<template>
    <div class="tag-management">
        <!-- 使用通用头部组件 -->
        <AdminPageHeader title="标签管理" subtitle="管理博客标签">
            <template #actions>
                <a-button type="primary" @click="showCreateDialog = true" :icon="h(PlusOutlined)">
                    新建标签
                </a-button>
            </template>
        </AdminPageHeader>

        <div class="tag-page-content">
            <!-- 加载状态 -->
            <a-spin v-if="isLoading" size="large" class="loading-spin">
                <div class="loading-content">
                    <p>正在加载标签列表...</p>
                </div>
            </a-spin>

            <!-- 错误状态 -->
            <a-result v-else-if="errorMessage" status="error" title="加载失败" :sub-title="errorMessage">
                <template #extra>
                    <a-button type="primary" @click="loadTags">重试</a-button>
                </template>
            </a-result>

            <!-- 标签列表 -->
            <div v-else>
                <a-row :gutter="[16, 16]" class="stats-cards">
                    <a-col :xs="24" :sm="12" :md="8">
                        <a-card :bordered="false">
                            <a-statistic title="标签总数" :value="tags.length" />
                        </a-card>
                    </a-col>
                    <a-col :xs="24" :sm="12" :md="8">
                        <a-card :bordered="false">
                            <a-statistic title="总引用次数" :value="totalTagUsage" />
                        </a-card>
                    </a-col>
                </a-row>

                <!-- 空状态 -->
                <a-empty v-if="tags.length === 0" description="还没有创建任何标签">
                    <a-button type="primary" @click="showCreateDialog = true">
                        创建第一个标签
                    </a-button>
                </a-empty>

                <!-- 标签列表 -->
                <a-row v-else :gutter="[16, 16]" class="tags-grid">
                    <a-col v-for="tag in tags" :key="tag.id" :xs="24" :sm="12" :md="8" :lg="6">
                        <!-- 使用通用卡片组件 -->
                        <AdminCard :title="tag.name" :color="tag.color">
                            <template #actions>
                                <a-tooltip title="编辑标签">
                                    <a-button type="text" size="small" @click="editTag(tag)" class="action-btn">
                                        <EditOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip title="删除标签">
                                    <a-button type="text" size="small" danger @click="deleteTag(tag)"
                                        class="action-btn">
                                        <DeleteOutlined />
                                    </a-button>
                                </a-tooltip>
                            </template>
                            <template #meta>
                                <a-typography-text type="secondary">
                                    <ClockCircleOutlined />
                                    {{ formatDate(tag.updated_at) }}
                                </a-typography-text>
                            </template>
                            <!-- 标签特有内容 -->
                            <template #default>
                                <div class="tag-content">
                                    <div class="tag-info">
                                        <a-statistic title="引用次数" :value="tag.usage_count || 0" suffix="次"
                                            :value-style="{ fontSize: '18px', lineHeight: '1.2' }" />
                                    </div>
                                </div>
                            </template>
                        </AdminCard>
                    </a-col>
                </a-row>
            </div>
        </div>

        <!-- 创建/编辑标签模态框 -->
        <a-modal :open="showCreateDialog || showEditDialog" :title="isEditing ? '编辑标签' : '新建标签'" @ok="submitForm"
            @cancel="closeDialogs" :confirm-loading="isSubmitting" :ok-text="isEditing ? '保存修改' : '创建标签'"
            cancel-text="取消">
            <a-form :model="formData" layout="vertical">
                <a-form-item label="标签名称" name="name" :rules="[{ required: true, message: '请输入标签名称' }]">
                    <a-input v-model:value="formData.name" placeholder="请输入标签名称" :maxlength="30" show-count />
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 删除确认模态框 -->
        <a-modal :open="showDeleteDialog" title="确认删除" @ok="confirmDelete" @cancel="closeDialogs"
            :confirm-loading="isSubmitting" ok-text="确认删除" cancel-text="取消" ok-type="danger">
            <p>确定要删除标签 <strong>"{{ tagToDelete?.name }}"</strong> 吗？</p>
            <div v-if="tagToDelete" class="delete-preview">
                <a-tag :color="tagToDelete.color" class="preview-tag">
                    <TagOutlined />
                    {{ tagToDelete.name }}
                </a-tag>
            </div>
            <a-alert message="此操作不可撤销！如果有文章使用该标签，则无法删除。" type="warning" show-icon class="delete-warning" />
        </a-modal>
    </div>
</template>

<script>
import { h } from 'vue'
import { message } from 'ant-design-vue'
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ClockCircleOutlined
} from '@ant-design/icons-vue'
import { getAllTags } from '../../services/dataService.js'
import supabaseTagService from '../../services/supabaseTagService.js'
import AdminPageHeader from '../../components/admin/AdminPageHeader.vue'
import AdminCard from '../../components/admin/AdminCard.vue'

export default {
    name: 'TagManagement',
    data() {
        return {
            tags: [],
            isLoading: true,
            errorMessage: null,

            // 对话框状态
            showCreateDialog: false,
            showEditDialog: false,
            showDeleteDialog: false,
            isEditing: false,
            isSubmitting: false,

            // 表单数据
            formData: {
                name: ''
            },

            // 编辑和删除相关
            editingTag: null,
            tagToDelete: null
        }
    },
    computed: {
        totalTagUsage() {
            return this.tags.reduce((total, tag) => total + (tag.usage_count || 0), 0)
        }
    },
    async mounted() {
        await this.loadData()
    },
    methods: {
        h,

        async loadData() {
            try {
                this.isLoading = true
                this.errorMessage = null
                const tags = await getAllTags()
                this.tags = tags.sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
            } catch (error) {
                console.error('加载数据失败:', error)
                this.errorMessage = '加载数据失败，请刷新重试'
            } finally {
                this.isLoading = false
            }
        },

        async loadTags() {
            await this.loadData()
        },

        // 编辑标签
        editTag(tag) {
            this.isEditing = true
            this.editingTag = tag
            this.formData = { name: tag.name }
            this.showEditDialog = true
        },

        // 删除标签
        deleteTag(tag) {
            this.tagToDelete = tag
            this.showDeleteDialog = true
        },

        // 提交表单
        async submitForm() {
            try {
                this.isSubmitting = true
                const operationName = this.isEditing ? '标签更新' : '标签创建'
                if (this.isEditing) {
                    await supabaseTagService.update(this.editingTag.id, this.formData)
                } else {
                    await supabaseTagService.create(this.formData)
                }
                await this.loadTags()
                this.closeDialogs()
                message.success(`${operationName}成功！`)
            } catch (error) {
                console.error('操作失败:', error)
                message.error(`操作失败：${error.message}`)
            } finally {
                this.isSubmitting = false
            }
        },

        // 确认删除
        async confirmDelete() {
            try {
                this.isSubmitting = true
                await supabaseTagService.remove(this.tagToDelete.id)
                await this.loadTags()
                this.closeDialogs()
                message.success('标签删除成功！')
            } catch (error) {
                console.error('删除失败:', error)
                message.error(`删除失败：${error.message}`)
            } finally {
                this.isSubmitting = false
            }
        },

        // 关闭对话框
        closeDialogs() {
            this.showCreateDialog = false
            this.showEditDialog = false
            this.showDeleteDialog = false
            this.isEditing = false
            this.editingTag = null
            this.tagToDelete = null
            this.formData = { name: '' }
        },

        // 格式化日期
        formatDate(dateString) {
            return new Date(dateString).toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        }
    },

    components: {
        PlusOutlined,
        EditOutlined,
        DeleteOutlined,
        ClockCircleOutlined,
        AdminPageHeader,
        AdminCard
    }
}
</script>

<style scoped>
.tag-management {
    background: #f0f2f5;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
}

.tag-page-content {
    padding: 24px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.loading-spin {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 0;
}

.loading-content {
    text-align: center;
    margin-top: 16px;
}

.stats-cards {
    margin-bottom: 24px;
    width: 100%;
    max-width: 100%;
}

.tags-grid {
    margin-top: 24px;
    width: 100%;
    max-width: 100%;
}

/* 标签特有样式 */
.tag-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    margin: 0;
}

.tag-info {
    flex: 1;
    margin: 0;
    padding: 0;
}

.tag-info :deep(.ant-statistic) {
    margin-bottom: 0;
    padding: 0;
}

.tag-info :deep(.ant-statistic-content) {
    font-size: 18px !important;
    line-height: 1.2 !important;
    margin: 0;
}

.tag-info :deep(.ant-statistic-title) {
    font-size: 13px !important;
    margin-bottom: 4px !important;
    color: #999 !important;
    line-height: 1.2 !important;
}

.tag-info :deep(.ant-statistic-content-suffix) {
    font-size: 13px !important;
}

.action-btn {
    padding: 4px !important;
    height: 24px !important;
    width: 24px !important;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
}

.action-btn:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.delete-preview {
    margin: 16px 0;
    text-align: center;
}

.delete-warning {
    margin-top: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .tag-page-content {
        padding: 16px;
    }

    .stats-cards {
        margin-bottom: 16px;
    }
}
</style>